import { BaseEffect } from '../BaseEffect.js';

/**
 * Pixel dissolve effect implementation
 * Creates a pixelated dissolve animation that breaks down the image into pixels that fade away
 */
export class DissolveEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            pixelSize: 8,          // Size of dissolve pixels (1-32 pixels)
            threshold: 0.5,        // Amount of image dissolved (0-1)
            edgeHardness: 0.1,    // Sharpness of dissolve edges
            noiseScale: 30.0,     // Scale of the noise pattern
            animate: true,        // Whether to animate the dissolve
            speed: 1.0           // Speed of the animation
        };

        super('pixel-dissolve', shaderManager, defaultParams);

        // Set up WebGL blending
        const gl = this.shaderManager.gl;
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_pixelSize;
            uniform float u_threshold;
            uniform float u_edgeHardness;
            uniform float u_noiseScale;
            
            varying vec2 v_texcoord;

            // Pseudo-random number generator
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // 2D noise function
            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);

                // Four corners of cell
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));

                // Smooth interpolation
                vec2 u = f * f * (3.0 - 2.0 * f);

                return mix(a, b, u.x) +
                        (c - a)* u.y * (1.0 - u.x) +
                        (d - b) * u.x * u.y;
            }

            void main() {
                // Calculate aspect-correct UV coordinates
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                // Create pixelated coordinates for the noise pattern
                vec2 pixelCoord = floor(uv * u_resolution / u_pixelSize) * u_pixelSize / u_resolution;
                
                // Generate noise value
                float n = noise(pixelCoord * u_noiseScale);
                
                // Add time-based movement to noise
                n = noise(pixelCoord * u_noiseScale + vec2(u_time * 0.1, u_time * 0.2));
                
                // Calculate dissolve threshold with edge smoothing
                float dissolve = smoothstep(
                    u_threshold - u_edgeHardness,
                    u_threshold + u_edgeHardness,
                    n
                );
                
                // Sample both original and pixelated textures
                vec4 originalColor = texture2D(u_image, uv);
                vec4 pixelColor = texture2D(u_image, pixelCoord);
                
                // Mix between pixelated and original based on dissolve value
                // When dissolve is 1, show original; when 0, show pixelated
                vec4 color = mix(pixelColor, originalColor, dissolve);
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_pixelSize', { type: '1f' });
        this.addUniform('u_threshold', { type: '1f' });
        this.addUniform('u_edgeHardness', { type: '1f' });
        this.addUniform('u_noiseScale', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        // Call parent class to handle common uniforms
        super.updateUniforms(uniforms);
        
        // Handle animation
        let currentThreshold = this.params.threshold;
        if (this.params.animate) {
            const time = uniforms.u_time ? uniforms.u_time.value : 0;
            currentThreshold = Math.sin(time * this.params.speed) * 0.5 + 0.5;
        }
        
        Object.assign(uniforms, {
            u_pixelSize: { value: this.params.pixelSize, type: '1f' },
            u_threshold: { value: currentThreshold, type: '1f' },
            u_edgeHardness: { value: this.params.edgeHardness, type: '1f' },
            u_noiseScale: { value: this.params.noiseScale, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            pixelSize: {
                min: 1,
                max: 32,
                step: 1,
                label: 'Pixel Size'
            },
            threshold: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Dissolve Amount'
            },
            edgeHardness: {
                min: 0.01,
                max: 0.5,
                step: 0.01,
                label: 'Edge Hardness'
            },
            noiseScale: {
                min: 1,
                max: 100,
                step: 1,
                label: 'Noise Scale'
            },
            speed: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Animation Speed'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 